import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import RewardCoin from '@/components/Icons/RewardCoin';
import { AchievementDefinition } from '@/redux/achievement/achievement.types';
import { categoryOf } from './achievementCategories';
import { BadgeState } from './useProfileAchievements';

const STAMP_TONE: Record<BadgeState, string> = {
  earned: 'bg-green-700 text-white shadow-sm',
  locked: 'bg-neutral-200/70 text-neutral-500',
  soon: 'bg-neutral-100 text-neutral-400',
};

const STAMP_SIZE = {
  md: { box: 'h-10 w-10', icon: 'h-[18px] w-[18px]' },
  sm: { box: 'h-6 w-6', icon: 'h-3.5 w-3.5' },
};

export function Stamp({
  entry,
  state,
  size = 'md',
}: {
  entry: AchievementDefinition;
  state: BadgeState;
  size?: keyof typeof STAMP_SIZE;
}) {
  const Icon = state === 'earned' ? categoryOf(entry.id).icon : Lock;
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        STAMP_SIZE[size].box,
        STAMP_TONE[state]
      )}
      aria-hidden
    >
      <Icon className={STAMP_SIZE[size].icon} strokeWidth={2} />
    </span>
  );
}

export function RewardChips({ entry }: { entry: AchievementDefinition }) {
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold tabular-nums">
      <span className="text-green-800">+{entry.points} XP</span>
      {entry.coins ? (
        <span className="inline-flex items-center gap-1 rounded-pill bg-amber-100 py-0.5 pl-0.5 pr-2 text-amber-700">
          <RewardCoin coinType="gold" width={16} height={16} />+{entry.coins}
        </span>
      ) : null}
    </span>
  );
}

export function CoinMark({ entry }: { entry: AchievementDefinition }) {
  if (!entry.coins) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums text-amber-700"
      data-testid={`achievement-${entry.id}-coins`}
      aria-label={`pays ${entry.coins} reward points`}
    >
      <RewardCoin coinType="gold" width={14} height={14} />+{entry.coins}
    </span>
  );
}
